/**
 * SpaceMouse Motion Simulator
 *
 * Genera eventos de movimiento sintéticos inyectándolos en SpaceMouseService,
 * que fluyen por el pipeline existente: onMotion → MotionBuffer → socket.emit.
 *
 * Recorre los 6 ejes (3 traslación + 3 rotación) en ambas direcciones (+/−),
 * produciendo 12 fases secuenciales en un loop de ~20 segundos.
 *
 * Uso desde la consola del navegador:
 *   __spacemouseSimulator.start()       // 20 segundos por defecto
 *   __spacemouseSimulator.start(30000)  // 30 segundos
 *   __spacemouseSimulator.stop()        // detener manualmente
 *   __spacemouseSimulator.isRunning     // consultar estado
 */

import { SpaceMouseService } from './SpaceMouseService';
import type { SpaceMouseMotionData } from './types';

// ---------------------------------------------------------------------------
// Phase definitions
// ---------------------------------------------------------------------------

interface SimulationPhase {
  /** Nombre legible para logs */
  label: string;
  /** Campo del eje afectado */
  axis: keyof Pick<SpaceMouseMotionData, 'dx' | 'dy' | 'dz' | 'rx' | 'ry' | 'rz'>;
  /** Valor delta por frame (~60Hz) */
  value: number;
}

/** Magnitud de los deltas — valores similares a un movimiento suave real */
const TRANSLATION_DELTA = 0.003;
const ROTATION_DELTA = 0.005;

const PHASES: SimulationPhase[] = [
  { label: '+dx  Traslación derecha',              axis: 'dx', value:  TRANSLATION_DELTA },
  { label: '-dx  Traslación izquierda',            axis: 'dx', value: -TRANSLATION_DELTA },
  { label: '+dy  Traslación arriba',               axis: 'dy', value:  TRANSLATION_DELTA },
  { label: '-dy  Traslación abajo',                axis: 'dy', value: -TRANSLATION_DELTA },
  { label: '+dz  Traslación atrás (zoom out)',      axis: 'dz', value:  TRANSLATION_DELTA },
  { label: '-dz  Traslación adelante (zoom in)',    axis: 'dz', value: -TRANSLATION_DELTA },
  { label: '+rx  Pitch arriba',                    axis: 'rx', value:  ROTATION_DELTA },
  { label: '-rx  Pitch abajo',                     axis: 'rx', value: -ROTATION_DELTA },
  { label: '+ry  Yaw derecha',                     axis: 'ry', value:  ROTATION_DELTA },
  { label: '-ry  Yaw izquierda',                   axis: 'ry', value: -ROTATION_DELTA },
  { label: '+rz  Roll clockwise',                  axis: 'rz', value:  ROTATION_DELTA },
  { label: '-rz  Roll counter-clockwise',          axis: 'rz', value: -ROTATION_DELTA },
];

const TICK_MS = 16; // ~60 Hz

// ---------------------------------------------------------------------------
// Simulator
// ---------------------------------------------------------------------------

export class SpaceMouseSimulator {
  private timer: ReturnType<typeof setInterval> | null = null;
  private startTime = 0;
  private durationMs = 20_000;
  private lastPhaseIndex = -1;

  /** True mientras la simulación está activa */
  get isRunning(): boolean {
    return this.timer !== null;
  }

  /**
   * Inicia la simulación.
   * @param durationMs Duración total en ms (default: 20 000 — 20 s)
   */
  start(durationMs = 20_000): void {
    if (this.isRunning) {
      console.warn('[SpaceMouseSimulator] Ya hay una simulación en curso. Llama a stop() primero.');
      return;
    }

    const service = SpaceMouseService.getInstance();

    // Advertir si nadie escucha motion (el pipeline dish→socket no estará conectado)
    const listeners = (service as any).listeners;
    const motionListeners = listeners?.motion;
    if (!motionListeners || motionListeners.length === 0) {
      console.warn(
        '[SpaceMouseSimulator] SpaceMouseService no tiene listeners en "motion". ' +
        'Asegúrate de que el componente dish esté montado y el socket conectado ' +
        'para que los eventos lleguen al servidor.'
      );
    }

    this.durationMs = durationMs;
    this.startTime = performance.now();
    this.lastPhaseIndex = -1;

    const phaseCount = PHASES.length;
    const phaseDuration = this.durationMs / phaseCount;

    console.log(
      `[SpaceMouseSimulator] ▶ Simulación iniciada — ${phaseCount} fases, ` +
      `${(phaseDuration / 1000).toFixed(1)}s por fase, ${(durationMs / 1000).toFixed(0)}s total`
    );

    this.timer = setInterval(() => {
      const elapsed = performance.now() - this.startTime;

      // ¿Terminó?
      if (elapsed >= this.durationMs) {
        this.stop();
        console.log('[SpaceMouseSimulator] ✔ Simulación completada.');
        return;
      }

      // Fase actual
      const phaseIndex = Math.min(
        Math.floor(elapsed / phaseDuration),
        phaseCount - 1,
      );

      // Log al cambiar de fase
      if (phaseIndex !== this.lastPhaseIndex) {
        this.lastPhaseIndex = phaseIndex;
        const phase = PHASES[phaseIndex];
        console.log(
          `[SpaceMouseSimulator] Fase ${phaseIndex + 1}/${phaseCount}: ${phase.label}`
        );
      }

      // Construir frame de motion — solo el eje activo tiene valor, el resto 0
      const phase = PHASES[phaseIndex];
      const motionData: SpaceMouseMotionData = {
        dx: 0,
        dy: 0,
        dz: 0,
        rx: 0,
        ry: 0,
        rz: 0,
        timestamp: performance.now(),
      };
      motionData[phase.axis] = phase.value;

      // Inyectar en el servicio — fluye por onMotion → MotionBuffer → socket
      service.emit('motion', motionData);
    }, TICK_MS);
  }

  /**
   * Detiene la simulación.
   */
  stop(): void {
    if (this.timer !== null) {
      clearInterval(this.timer);
      this.timer = null;
      this.lastPhaseIndex = -1;
      console.log('[SpaceMouseSimulator] ⏹ Simulación detenida.');
    }
  }
}

// ---------------------------------------------------------------------------
// Registrar en window para acceso desde DevTools
// ---------------------------------------------------------------------------

const simulatorInstance = new SpaceMouseSimulator();

(window as any).__spacemouseSimulator = simulatorInstance;

// Log informativo al cargar el módulo
console.log(
  '[SpaceMouseSimulator] Disponible en consola:\n' +
  '  __spacemouseSimulator.start()       → Iniciar simulación (20s)\n' +
  '  __spacemouseSimulator.start(30000)  → Duración personalizada\n' +
  '  __spacemouseSimulator.stop()        → Detener\n' +
  '  __spacemouseSimulator.isRunning     → Estado'
);
